import { useEffect, useRef, useState } from "react";
import { CommerceDialog } from "../../commerce/CommerceDialog";
import { toast } from "../../../hooks/useToast";
import { definirInfiniteTag } from "../../../utils/pagamentos";
import { Icone } from "./Icone";
import { comArtigo, gatewayPorId, termosAceitos } from "./gateways";

/* Conectar ou trocar de provedor, numa janela só:
     troca    (só se outro provedor estiver conectado) → explica e pede confirmação
     termos   o essencial antes de autorizar + contador de TEMPO segundos
              (só na primeira vez ou quando os termos mudam)
     → abre o provedor (OAuth) ou, na InfinitePay, salva a tag
   O provedor atual só é desconectado pelo servidor DEPOIS que o novo
   concluir; se algo falhar aqui, nada muda. */

export const TEMPO_LEITURA = 10;
const maiuscula = t => t.charAt(0).toUpperCase() + t.slice(1);

function useContagem(total, ativo) {
  const [restante, setRestante] = useState(ativo ? total : 0);
  useEffect(() => {
    if (!ativo) { setRestante(0); return undefined; }
    setRestante(total);
    const inicio = Date.now();
    const id = setInterval(() => {
      const r = Math.max(0, total - Math.floor((Date.now() - inicio) / 1000));
      setRestante(r);
      if (r === 0) clearInterval(id);
    }, 200);
    return () => clearInterval(id);
  }, [total, ativo]);
  return restante;
}

function Logo({ gw }) {
  return <div className="pgw-logo" style={{ "--gw-cor": gw.cor }}>{gw.sigla}</div>;
}

export function FluxoConexao({ gw, status, atual, onClose, onConcluido }) {
  const trocando = !!atual && atual.gw.id !== gw.id;
  const [etapa, setEtapa] = useState(trocando ? "troca" : "termos");
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState("");
  const [tag, setTag] = useState("");
  const aberto = useRef(true);
  useEffect(() => () => { aberto.current = false; }, []);

  const precisaEsperar = !termosAceitos(gw, status);
  const restante = useContagem(TEMPO_LEITURA, etapa === "termos" && precisaEsperar);
  const podeAqui = gw.podeConectarAqui();
  const opcoes = { termos: gw.termosVersao, ...(trocando ? { trocar: atual.gw.id } : {}) };

  async function continuar() {
    setErro("");
    if (gw.pedeTag && !tag.trim()) { setErro("Informe a InfiniteTag."); return; }
    setOcupado(true);
    try {
      if (gw.pedeTag) {
        const r = await definirInfiniteTag(tag.trim(), opcoes);
        onConcluido?.(gw, r);
        onClose();
        return;
      }
      // Leva ao site oficial do provedor; a volta conclui em Configurações → Pagamentos
      await gw.conectar(opcoes);
    } catch (e) {
      if (!aberto.current) return;
      setErro(gw.mensagemErro(e));
      setOcupado(false);
    }
  }

  const fechar = () => { if (!ocupado) onClose(); };
  const cor = { "--gw-cor": gw.cor, "--b-cor": gw.cor };

  // Desktop/Android: a autorização volta para o site, então a conexão é feita lá
  if (!podeAqui) {
    return (
      <CommerceDialog className="pgw-dialogo" title={`Conectar ${gw.nome}`} subtitle="A conexão é feita pelo site do Print Control" onClose={onClose}
        footer={<>
          <button type="button" className="pgw-btn" onClick={onClose}>Fechar</button>
          <a className="pgw-btn primario" style={cor} href={gw.site} target="_blank" rel="noopener noreferrer">Abrir o site <Icone nome="seta" width="16" height="16" /></a>
        </>}>
        <div className="pgw-etapa" style={cor}>
          <p>Depois que você autoriza, {comArtigo(gw)} devolve você para o site do Print Control. Abra o site no navegador, entre em <strong>Configurações → Pagamentos</strong> e conecte por lá.</p>
          <p>Com a conta conectada, as cobranças funcionam também neste aplicativo.</p>
        </div>
      </CommerceDialog>
    );
  }

  if (etapa === "troca") {
    const contaAtual = atual.gw.conta(atual.status);
    return (
      <CommerceDialog className="pgw-dialogo" title={`Trocar para ${gw.nome}?`} subtitle="Sua empresa recebe por um provedor de cada vez" onClose={fechar}
        footer={<>
          <button type="button" className="pgw-btn" onClick={onClose}>Cancelar</button>
          <button type="button" className="pgw-btn primario" style={cor} onClick={() => setEtapa("termos")}>Continuar <Icone nome="seta" width="16" height="16" /></button>
        </>}>
        <div className="pgw-etapa" style={cor}>
          <div className="pgw-troca" aria-hidden="true">
            <div className="pgw-troca-lado"><Logo gw={atual.gw} /><span>{atual.gw.nome}</span><small>Hoje</small></div>
            <Icone nome="seta" />
            <div className="pgw-troca-lado"><Logo gw={gw} /><span>{gw.nome}</span><small>Depois</small></div>
          </div>
          <p>Hoje sua empresa recebe pelo <strong>{atual.gw.nome}</strong>{contaAtual ? <> ({contaAtual})</> : null}. Para usar {comArtigo(gw)}, {comArtigo(atual.gw)} será desconectado.</p>
          <ul className="pgw-passos">
            <li><span><Icone nome="escudo" /></span><div>{maiuscula(comArtigo(atual.gw))} continua funcionando até {comArtigo(gw)} terminar de conectar. Só então ele é desconectado.</div></li>
            <li><span><Icone nome="voltar" /></span><div>Se você cancelar ou algo der errado, nada muda.</div></li>
            <li><span><Icone nome="alerta" /></span><div>Cobranças do {atual.gw.nome} ainda em aberto deixam de ser confirmadas aqui; confira-as no próprio {atual.gw.nome}.</div></li>
          </ul>
        </div>
      </CommerceDialog>
    );
  }

  const rotulo = ocupado
    ? <><span className="pgw-spin" aria-hidden="true" /> {gw.pedeTag ? "Salvando…" : `Abrindo ${comArtigo(gw)}…`}</>
    : restante > 0 ? `Continuar em ${restante}s`
    : gw.pedeTag ? `Configurar ${gw.nome}` : `Continuar para ${comArtigo(gw)}`;

  return (
    <CommerceDialog className="pgw-dialogo" title={`${status?.precisaReconectar ? "Reconectar" : "Conectar"} ${gw.nome}`}
      subtitle={precisaEsperar ? "Leia com atenção antes de continuar" : "Você já conhece estas condições"} onClose={fechar}
      footer={<>
        <button type="button" className="pgw-btn" onClick={() => (trocando ? setEtapa("troca") : onClose())} disabled={ocupado}>{trocando ? "Voltar" : "Cancelar"}</button>
        <button type="button" className="pgw-btn primario pgw-contador" style={{ ...cor, "--pgw-tempo": `${TEMPO_LEITURA}s` }}
          onClick={continuar} disabled={restante > 0 || ocupado} aria-describedby={precisaEsperar ? "pgw-aviso-contador" : undefined}>
          {restante > 0 && <i className="pgw-barra" aria-hidden="true" />}
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>{rotulo}</span>
        </button>
      </>}>
      <div className="pgw-etapa" style={cor}>
        {gw.emTeste() && <span className="pgw-tag" style={{ alignSelf: "flex-start" }}>{gw.id === "infinitepay" ? "InfinitePay — ambiente de teste / integração em desenvolvimento" : "Ambiente de teste"}</span>}
        {trocando && <div className="pgw-destaque info">Ao concluir, <strong>{comArtigo(atual.gw)}</strong> será desconectado.</div>}
        <ul className="pgw-passos">
          {gw.antesDeConectar.map(p => <li key={p.texto}><span><Icone nome={p.icone} /></span><div>{p.texto}</div></li>)}
        </ul>
        {gw.pedeTag && (
          <label className="pgw-campo">
            InfiniteTag
            <div className="pgw-prefixo"><span aria-hidden="true">$</span>
              <input value={tag} onChange={e => setTag(e.target.value.replace(/^\$+/, ""))} placeholder="suainfinitetag"
                autoCapitalize="none" autoComplete="off" spellCheck={false} maxLength={41} disabled={ocupado} aria-label="InfiniteTag" />
            </div>
            <small>A que aparece no app InfinitePay. Os pagamentos vão para a conta dona dela.</small>
          </label>
        )}
        {erro && <div className="pgw-erro" role="alert">{erro}</div>}
        <details className="pgw-detalhes">
          <summary>Detalhes de segurança</summary>
          <ul>{gw.seguranca.map(s => <li key={s}>{s}</li>)}</ul>
        </details>
        {precisaEsperar && <span id="pgw-aviso-contador" className="pgw-sr">O botão é liberado em {TEMPO_LEITURA} segundos, para você ler as informações.</span>}
      </div>
    </CommerceDialog>
  );
}

// Retorno bem-sucedido de uma conexão (OAuth concluído ou tag salva)
export function avisarConectado(gw, resposta) {
  const nome = id => { const g = gatewayPorId(id); return g ? comArtigo(g) : id; };
  const trocou = resposta?.trocouDe ? ` ${maiuscula(nome(resposta.trocouDe))} foi desconectado.` : "";
  toast(`${gw.nome} conectado.${trocou}`, "success");
  if (resposta?.trocaPendente) {
    toast(`Não foi possível desconectar ${nome(resposta.trocaPendente)} automaticamente. Desconecte em Pagamentos → Gerenciar.`, "error", 8000);
  }
}
